import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import api from '../../config/roundrobinApi';
import { useAuth } from '../../context/AuthContext';

const AUTH_BASE = process.env.REACT_APP_AUTH_API_BASE || 'http://localhost:8003';

export default function SettingsTab() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [connectionMsg, setConnectionMsg] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings');
      setSettings(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const checkOAuthStatus = useCallback(async () => {
    setCheckingStatus(true);
    try {
      const res = await api.post('/settings/check-oauth-status');
      setOauthStatus(res.data);
    } catch (err) { console.error(err); }
    finally { setCheckingStatus(false); }
  }, []);

  useEffect(() => {
    fetchSettings();
    checkOAuthStatus();
  }, [fetchSettings, checkOAuthStatus]);

  // Listen for OAuth popup completion
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'oauth-success') {
        setConnectionMsg({ type: 'success', text: 'Zendesk connected successfully!' });
        checkOAuthStatus();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [checkOAuthStatus]);

  const handleConnectZendesk = () => {
    const subdomain = user?.subdomain;
    if (!subdomain) {
      setConnectionMsg({ type: 'danger', text: 'No subdomain found in session' });
      return;
    }
    const email = user?.email || '';
    const url = `${AUTH_BASE}/oauth/initiate-popup?subdomain=${encodeURIComponent(subdomain)}&email=${encodeURIComponent(email)}`;
    window.open(url, 'zendesk-oauth', 'width=600,height=700');
  };

  const handleTestConnection = async () => {
    setConnectionMsg(null);
    try {
      const res = await api.post('/settings/test-connection');
      setConnectionMsg({ type: res.data.success ? 'success' : 'danger', text: res.data.message });
    } catch (err) {
      setConnectionMsg({ type: 'danger', text: 'Connection test failed' });
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        global_limit_per_agent: settings.global_limit_per_agent,
        same_agent_period: settings.same_agent_period,
        load_balancing_period: settings.load_balancing_period,
        ooo_add_to_ticket: settings.ooo_add_to_ticket,
        ooo_comment_text: settings.ooo_comment_text,
        availability_app_project_id: settings.availability_app_project_id,
        availability_app_api_key: settings.availability_app_api_key === '****' ? undefined : settings.availability_app_api_key,
      });
      setMessage({ type: 'success', text: 'Settings saved' });
    } catch (err) { setMessage({ type: 'danger', text: 'Save failed' }); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <>
      {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Card className="mb-3">
        <Card.Header><strong>Zendesk Connection</strong></Card.Header>
        <Card.Body>
          {connectionMsg && <Alert variant={connectionMsg.type} dismissible onClose={() => setConnectionMsg(null)}>{connectionMsg.text}</Alert>}

          <div className="d-flex align-items-center mb-3">
            <div className="me-3">
              <strong>Subdomain:</strong>{' '}
              <span style={{ color: '#6366f1' }}>{user?.subdomain || 'N/A'}</span>
            </div>
            <div className="me-3">
              <strong>Status:</strong>{' '}
              {checkingStatus ? (
                <Spinner size="sm" />
              ) : oauthStatus?.connected ? (
                <Badge bg="success">Connected</Badge>
              ) : (
                <Badge bg="warning">Not Connected</Badge>
              )}
            </div>
            {oauthStatus?.email && (
              <div>
                <strong>Account:</strong>{' '}
                <span className="text-muted">{oauthStatus.email}</span>
              </div>
            )}
          </div>

          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleConnectZendesk}>
              {oauthStatus?.connected ? 'Reconnect with Zendesk' : 'Connect with Zendesk'}
            </Button>
            {oauthStatus?.connected && (
              <Button variant="outline-primary" onClick={handleTestConnection}>
                Test Connection
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><strong>Parameters</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Label>Limit per Agent</Form.Label>
              <Form.Control type="number" min={0} value={settings.global_limit_per_agent ?? ''} onChange={e => updateField('global_limit_per_agent', e.target.value ? parseInt(e.target.value) : null)} />
            </Col>
            <Col md={4}>
              <Form.Label>Same Agent Period</Form.Label>
              <Form.Control value={settings.same_agent_period || ''} onChange={e => updateField('same_agent_period', e.target.value)} placeholder="2h" pattern="^\d+[hmd]$" isInvalid={settings.same_agent_period && !/^\d+[hmd]$/.test(settings.same_agent_period)} />
              <Form.Control.Feedback type="invalid">Must be a number followed by h, m, or d (e.g. "2h")</Form.Control.Feedback>
              <Form.Text className="text-muted">e.g. "2h", "30m", "1d" - prevent same requester-agent within this window</Form.Text>
            </Col>
            <Col md={4}>
              <Form.Label>Load Balancing Period</Form.Label>
              <Form.Control value={settings.load_balancing_period || ''} onChange={e => updateField('load_balancing_period', e.target.value)} placeholder="24h" pattern="^\d+[hmd]$" isInvalid={settings.load_balancing_period && !/^\d+[hmd]$/.test(settings.load_balancing_period)} />
              <Form.Control.Feedback type="invalid">Must be a number followed by h, m, or d (e.g. "24h")</Form.Control.Feedback>
              <Form.Text className="text-muted">Period for resetting assignment counts</Form.Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><strong>Agent Availability App</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Label>Project ID</Form.Label>
              <Form.Control value={settings.availability_app_project_id || ''} onChange={e => updateField('availability_app_project_id', e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>API Key</Form.Label>
              <Form.Control type="password" value={settings.availability_app_api_key || ''} onChange={e => updateField('availability_app_api_key', e.target.value)} placeholder={settings.availability_app_api_key === '****' ? '****' : 'Enter API key'} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><strong>Out of Office</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Label>Add to Ticket</Form.Label>
              <Form.Select value={settings.ooo_add_to_ticket || 'private comment'} onChange={e => updateField('ooo_add_to_ticket', e.target.value)}>
                <option value="private comment">Private Comment</option>
                <option value="public comment">Public Comment</option>
              </Form.Select>
            </Col>
            <Col md={8}>
              <Form.Label>Comment</Form.Label>
              <Form.Control as="textarea" rows={3} value={settings.ooo_comment_text || ''} onChange={e => updateField('ooo_comment_text', e.target.value)} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mb-4">
        <Button variant="primary" size="lg" onClick={handleSaveSettings} disabled={saving}>
          {saving ? <><Spinner size="sm" className="me-1" />Saving...</> : 'Save Settings'}
        </Button>
      </div>
    </>
  );
}
